import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { waLink, WA_DEFAULT_MSG } from "@/lib/whatsapp";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const TRUST_POINTS = ["Mutual Funds & SIP", "Fixed Income", "Term Insurance"];

export const Hero = () => (
  <section id="top" className="pt-28 lg:pt-40 pb-16 lg:pb-24 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
      <div className="lg:col-span-7">
        <motion.p
          {...fadeUp}
          transition={{ duration: 0.5 }}
          data-testid="hero-overline"
          className="text-xs font-semibold uppercase tracking-[0.2em] text-bronze"
        >
          Rajkiran Wealth — Personal Finance Advisory
        </motion.p>
        <motion.h1
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-forest"
        >
          Grow your wealth.
          <br />
          Protect your family.
        </motion.h1>
        <motion.p
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl"
        >
          One trusted advisor for mutual funds, stable fixed income, and term
          insurance — so every rupee you earn works towards the life you want.
        </motion.p>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 flex flex-col sm:flex-row gap-4"
        >
          <Button
            asChild
            size="lg"
            data-testid="hero-callback-btn"
            className="bg-forest text-bone hover:bg-forest-light rounded-full px-8 transition-transform hover:-translate-y-0.5"
          >
            <a href="#contact">Get a Free Call Back</a>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            data-testid="hero-whatsapp-btn"
            className="border-forest text-forest hover:bg-forest hover:text-bone rounded-full px-8 transition-transform hover:-translate-y-0.5"
          >
            <a href={waLink(WA_DEFAULT_MSG)} target="_blank" rel="noopener noreferrer">
              <WhatsAppIcon className="h-5 w-5 mr-2" />
              WhatsApp Us
            </a>
          </Button>
        </motion.div>

        <motion.ul
          {...fadeUp}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8 flex flex-wrap gap-x-6 gap-y-2"
        >
          {TRUST_POINTS.map((t) => (
            <li key={t} className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-bronze" />
              {t}
            </li>
          ))}
        </motion.ul>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.25 }}
        className="lg:col-span-5 relative"
      >
        <div className="absolute -top-4 -right-4 w-full h-full border border-bronze/50 rounded-2xl" aria-hidden="true" />
        <img
          src="https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg3NTQ4ODk1fDA&ixlib=rb-4.1.0&q=85"
          alt="Structured modern architecture symbolising stable wealth"
          data-testid="hero-image"
          className="relative rounded-2xl object-cover w-full max-h-[50vh] lg:max-h-[70vh] lg:h-[540px]"
        />
        <div className="absolute -bottom-6 -left-4 lg:-left-10 bg-forest text-bone rounded-xl px-6 py-4 shadow-lg">
          <p className="font-serif text-3xl font-medium" data-testid="hero-stat-number">500+</p>
          <p className="text-xs tracking-wide text-bone/80">Families guided with discipline</p>
        </div>
      </motion.div>
    </div>
  </section>
);
